//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import template from './bulk-selection-overlay-container.html';

import angular, { ILogService, IPromise, IQService } from 'angular';
import { INgRedux } from 'ng-redux';
import _ from 'underscore';

import { ICuralateStore } from '@models/AppState';
import PartialCuid from '@models/PartialCuid';
import '../bulk-actions-actions/bulk-actions-actions';
import '../bulk-selection-overlay/bulk-selection-overlay';
import '../model/ISelectedItem';
import '../reducers/bulk-actions-reducer';

export class BulkSelectionOverlayContainer {
  public isSelected: boolean;
  public isAnyItemSelected: boolean;
  public itemDetails: any;
  public itemType: string;
  // The base item ID, which is the real itemId most of the time but not always correct in Explore
  public itemId: string;
  public isSmallImage: boolean = false;
  public hasItemDetailsSubscription: boolean = false;
  public itemDetailsSubscription;
  public setItemDetails;
  public updateItemOverlayDetails;
  public openUia;
  public onImageClickedCallback;
  public trueItemIdPromise: IPromise<any>;
  // The real item ID, which is itemId most of the time but computed from itemIdCallback in Explore
  public trueItemId: string;

  private readonly MIN_IMAGE_SIZE_FOR_ITEM_DETAILS: number = 50;
  private unsubscribe: () => void;
  private itemIdCallback;
  private alwaysShowSelection?: boolean;
  private getTrueItemIdOnInit?: boolean;

  public constructor(
    private $element,
    private $ngRedux: INgRedux,
    private $q: IQService,
    private $log: ILogService,
    private BulkActionsActions,
  ) {}

  public $onDestroy() {
    this.unsubscribe?.();

    if (this.itemDetailsSubscription) {
      this.itemDetailsSubscription.unsubscribe();
    }
  }

  public $onInit() {
    const actions = Object.assign({}, this.BulkActionsActions);

    this.unsubscribe = this.$ngRedux.connect(
      this.mapStateToThis,
      actions,
    )(this);

    if (this.itemType && this.itemId) {
      this.handleItemDetailsSubscription();
      this.hasItemDetailsSubscription = true;
    } else {
      // if itemType or itemId are unknown on init, log it out
      // we have seen intermittent errors from this situation so want to be aware when it happens
      this.$log.error(
        'Unknown itemId or itemType on bulk-selection-overlay-container initialization.',
        window.location.href,
      );
    }
  }

  public $onChanges(changeObj) {
    // Guard: $onChanges fires before $onInit for initial bindings.
    // updateItemOverlayDetails is only available after $ngRedux.connect() in $onInit.
    if (
      !this.hasItemDetailsSubscription &&
      this.updateItemOverlayDetails &&
      changeObj.itemType?.currentValue &&
      changeObj.itemId?.currentValue
    ) {
      this.handleItemDetailsSubscription();
      this.hasItemDetailsSubscription = true;
    }
  }

  public handleItemDetailsSubscription() {
    const partialCuid = PartialCuid(`${this.itemType}.${this.itemId}`);

    this.itemDetailsSubscription = this.updateItemOverlayDetails(
      partialCuid,
    ).subscribe((response) => {
      if (response && response[partialCuid]) {
        this.setItemDetails(partialCuid, response[partialCuid]);
      }
    });
  }

  public getTrueCuid(): IPromise<string> {
    if (!this.trueItemIdPromise) {
      const trueItemIdBasePromise = this.itemIdCallback() || this.$q.when({});
      this.trueItemIdPromise = trueItemIdBasePromise.then((response) => {
        this.trueItemId = (response && response.updatedItemId) || this.itemId;
        return this.trueItemId;
      });
    }
    return this.trueItemIdPromise;
  }

  public onImageClicked(
    itemId: string,
    itemType: string,
    page?: string,
    context?: any,
  ) {
    this.openUia(itemId, itemType, page, context);
    this.onImageClickedCallback();
  }

  private setIsSelected = (itemType, trueItemId, selectedCuids) => {
    const partialCuid = `${itemType}.${trueItemId}`;
    this.isSelected = _(selectedCuids).contains(partialCuid);
  };

  private mapStateToThis = (state: ICuralateStore) => {
    const bulkActionsState = state.bulkActions;
    const partialCuid = `${this.itemType}.${this.itemId}`;

    const selectedItems = bulkActionsState.selectedItems;
    const selectedCuids = _(selectedItems).pluck('cuid');
    const isAnyItemSelected =
      this.alwaysShowSelection || !_(selectedCuids).isEmpty();

    const allItemDetails = bulkActionsState.allItemDetails;
    const itemDetails = allItemDetails && allItemDetails[partialCuid];

    if (this.trueItemId) {
      this.setIsSelected(this.itemType, this.trueItemId, selectedCuids);
    } else if (this.getTrueItemIdOnInit) {
      this.getTrueCuid().then(() =>
        this.setIsSelected(this.itemType, this.trueItemId, selectedCuids),
      );
    }

    return {
      isAnyItemSelected,
      itemDetails,
    };
  };
}

angular.module('curalate').component('bulkSelectionOverlayContainer', {
  bindings: {
    context: '<',
    disableBulkActions: '<',
    showInfoIcon: '<?',
    itemId: '<',
    itemIdCallback: '&', // An optional callback to compute the true itemId -- needed in Explore
    itemType: '<',
    onAddToGalleryCallback: '&', // Two optional callbacks used for sending
    onImageClickedCallback: '&', // GA events in content discovery
    page: '<',
    alwaysShowSelection: '<',
    shouldShowGalleryText: '<',
    shouldShowConnectText: '<',
    hideItemDetails: '<',
    getTrueItemIdOnInit: '<',
    isSmallImage: '<',
  },
  controller: [
    '$element',
    '$ngRedux',
    '$q',
    '$log',
    'BulkActionsActions',
    BulkSelectionOverlayContainer,
  ],
  template,
});
